import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wallet, Loader2, CheckSquare, Square, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtUsd, fmtPrice, fmtPct } from "@/lib/format";

const pnlCls = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-red-400" : "text-slate-500");

export default function WalletImportDialog({ open, setOpen, onImported, initialPreview }) {
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [entries, setEntries] = useState({});
  const [committing, setCommitting] = useState(false);
  const [label, setLabel] = useState("");
  const [help, setHelp] = useState(false);
  useEffect(() => { if (open && initialPreview) { setPreview(initialPreview); setPicked(new Set()); setEntries({}); } }, [open, initialPreview]);

  const reset = () => { setPreview(null); setPicked(new Set()); setEntries({}); setAddress(""); setLabel(""); };
  const close = (o) => { setOpen(o); if (!o) reset(); };

  const scan = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/trades/import/wallet/preview", { address: address.trim() });
      setPreview(data);
      setPicked(new Set(data.trades.filter((t) => !t.duplicate && !t.orphanSell).map((t) => t.key)));
      if (data.trades.length === 0) toast("No SOL/USDC ↔ token swaps found in this wallet's recent history");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Import failed"); }
    finally { setLoading(false); }
  };

  const toggle = (k) => setPicked((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const all = () => setPicked(new Set(preview.trades.filter((t) => !t.duplicate && (!t.orphanSell || Number(entries[t.key]) > 0)).map((t) => t.key)));
  const ready = [...picked].filter((k) => { const t = preview?.trades.find((x) => x.key === k); return t && (!t.orphanSell || Number(entries[k]) > 0); });

  const commit = async () => {
    setCommitting(true);
    try {
      const trades = preview.trades.filter((t) => ready.includes(t.key)).map((t) => ({
        ticker: t.ticker, name: t.name, address: t.address, chain: "solana", buyPrice: t.buyPrice ?? Number(entries[t.key]), sellPrice: t.sellPrice,
        amount: t.amount, status: t.status, mood: "", notes: t.notes, date: t.date,
        entryMcap: t.entryMcap, exitMcap: t.exitMcap, holdHours: t.holdHours, firstBuyAt: t.firstBuyAt, lastSellAt: t.lastSellAt, fills: t.fills,
      }));
      const { data } = await api.post("/trades/import/wallet/commit", { address: preview.address, label: label || undefined, trades });
      toast.success(`Imported ${data.imported} trade${data.imported === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} already in your journal` : ""}`);
      onImported?.();
      close(false);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Import failed"); }
    finally { setCommitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="glass border-[#334566] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wallet-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Wallet className="h-5 w-5 text-emerald-400" /> Import from wallet</DialogTitle>
          <DialogDescription className="text-slate-400">Paste a Solana wallet. We read its swaps on-chain and turn each coin into a journal entry — buys, sells, size and P&L. Nothing to connect, nothing to sign.</DialogDescription>
        </DialogHeader>

        {!preview && (
          <>
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-3.5 space-y-2" data-testid="wallet-safety-panel">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-300"><ShieldCheck className="h-4 w-4" /> Why this is safe</div>
              <ul className="text-xs text-slate-300 space-y-1 leading-relaxed">
                <li>• We only <b>read your public address</b> — the same thing anyone can look up on Solscan.</li>
                <li>• <b>No wallet connect, no signature, no seed phrase, no private key.</b> There is nothing to approve, so there is nothing we could ever move.</li>
                <li>• Never type a seed phrase into any website — including this one. We will never ask for it.</li>
              </ul>
              <button type="button" onClick={() => setHelp((h) => !h)} className="text-[11px] text-emerald-400 hover:underline" data-testid="wallet-where-btn">Where do I find my address? {help ? "▴" : "▾"}</button>
              {help && (
                <ul className="text-[11px] text-slate-400 space-y-1" data-testid="wallet-where-help">
                  <li><b className="text-slate-300">FOMO</b> — Wallet tab → tap your balance / Receive → copy the Solana address.</li>
                  <li><b className="text-slate-300">pump.fun</b> — Profile (top right) → your address is under your name, tap to copy.</li>
                  <li><b className="text-slate-300">Phantom</b> — tap the account name at the top → Solana → copy address.</li>
                  <li><b className="text-slate-300">Photon / Axiom / Trojan</b> — Settings or Wallet → copy address. Trading bots create their own wallet — import that one.</li>
                </ul>
              )}
            </div>
            <form onSubmit={scan} className="space-y-2">
              <div className="flex gap-2">
                <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Solana wallet address" autoFocus
                  className="bg-[#0d1420] border-[#334566] font-mono text-sm h-11" data-testid="wallet-address-input" />
                <Button type="submit" disabled={loading || address.trim().length < 32} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold h-11 px-5" data-testid="wallet-scan-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Scan <ArrowRight className="h-4 w-4 ml-1" /></>}
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="text-slate-500 mr-1">Label (optional):</span>
                {["FOMO", "pump.fun", "Phantom", "Photon", "Axiom", "Bot"].map((l) => (
                  <button key={l} type="button" onClick={() => setLabel(label === l ? "" : l)} data-testid={`wallet-label-${l}`}
                    className={`tap px-2 h-6 rounded-full border font-mono ${label === l ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-300" : "border-[#334566] text-slate-400"}`}>{l}</button>
                ))}
                <input value={label} onChange={(e) => setLabel(e.target.value.slice(0, 24))} placeholder="custom" data-testid="wallet-label-input"
                  className="h-6 w-40 rounded-full bg-[#0d1420] border border-[#334566] px-2 font-mono text-[11px] text-slate-100" />
              </div>
            </form>
          </>
        )}

        {preview && (
          <>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono text-slate-500" data-testid="wallet-preview-meta">
              <span className="text-slate-300">{preview.address.slice(0, 4)}…{preview.address.slice(-4)}</span>
              <span>{preview.swapsScanned} txs · {preview.buyFills} buys · {preview.sellFills} sells · {preview.trades.length} coins</span>
              <span>SOL {fmtUsd(preview.solUsd)}</span>
              {!preview.syncOnly && <button onClick={reset} className="ml-auto text-emerald-400 hover:underline" data-testid="wallet-change-btn">change wallet</button>}
            </div>
            {preview.syncOnly && <p className="text-xs text-amber-300/90" data-testid="wallet-sync-orphans-note">These coins were sold from this wallet but bought before the scanned history. Add an entry price to import them with P&L.</p>}
            {preview.trades.length > 0 && (
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>{picked.size} selected</span>
                <button onClick={all} className="hover:text-emerald-400" data-testid="wallet-select-all">select all new</button>
              </div>
            )}
            <div className="space-y-1.5 max-h-[48vh] overflow-y-auto pr-1" data-testid="wallet-trade-list">
              {preview.trades.map((t) => {
                const on = picked.has(t.key);
                return (
                  <button key={t.key} type="button" onClick={() => !t.duplicate && toggle(t.key)} disabled={t.duplicate} data-testid={`wallet-trade-${t.ticker}`}
                    className={`tap w-full text-left flex items-center gap-3 rounded-xl border p-3 transition-colors ${t.duplicate ? "opacity-45 border-[#222F43]" : on ? "border-emerald-500/50 bg-emerald-500/8" : "border-[#222F43] bg-[#111723] hover:border-[#334566]"}`}>
                    {t.duplicate ? <Square className="h-4 w-4 text-slate-600 shrink-0" /> : on ? <CheckSquare className="h-4 w-4 text-emerald-400 shrink-0" /> : <Square className="h-4 w-4 text-slate-500 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold">${t.ticker}</span>
                        <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${t.status === "closed" ? "border-slate-600 text-slate-400" : "border-cyan-500/40 text-cyan-400"}`}>{t.status}{t.status === "open" && t.soldPct > 0 ? ` · ${t.soldPct}% sold` : ""}</span>
                        {t.duplicate && <span className="text-[9px] font-mono uppercase text-amber-400">already in journal</span>}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono truncate">{t.buys} buy{t.buys !== 1 ? "s" : ""} · {t.sells} sell{t.sells !== 1 ? "s" : ""} · {t.orphanSell ? `out ${fmtPrice(t.sellPrice)}` : `in ${fmtPrice(t.buyPrice)}${t.sellPrice ? ` → out ${fmtPrice(t.sellPrice)}` : ""}`} · {t.date?.slice(0, 10)}</div>
                      {t.orphanSell && !t.duplicate && (
                        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-amber-300/90" onClick={(e) => e.stopPropagation()}>
                          <span className="shrink-0">Bought before scanned history — entry price:</span>
                          <input type="number" step="any" min="0" placeholder="0.000123" value={entries[t.key] || ""} data-testid={`wallet-entry-${t.ticker}`}
                            onChange={(e) => { const v = e.target.value; setEntries((s) => ({ ...s, [t.key]: v })); if (Number(v) > 0) setPicked((s) => new Set(s).add(t.key)); }}
                            className="w-28 h-7 rounded bg-[#0d1420] border border-[#334566] px-2 font-mono text-xs text-slate-100" />
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-sm">{fmtUsd(t.amount)}</div>
                      {t.orphanSell
                        ? <div className="text-[11px] font-mono text-slate-500">received</div>
                        : t.status === "closed"
                        ? <div className={`text-[11px] font-mono ${pnlCls(t.realizedPnl)}`}>{fmtUsd(t.realizedPnl)} · {fmtPct(t.amount ? (t.realizedPnl / t.amount) * 100 : 0)}</div>
                        : <div className="text-[11px] font-mono text-slate-500">open</div>}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-500 font-mono">{preview.note} Token-to-token swaps are skipped. Entry market cap, hold time and every fill come along. Edit anything after import.</p>
            <DialogFooter>
              <Button onClick={commit} disabled={committing || ready.length === 0} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="wallet-import-btn">
                {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import ${ready.length} trade${ready.length === 1 ? "" : "s"}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
